<script lang="ts">
  import { onMount } from 'svelte';
  import { getBitcoinPlusPlusConferences, searchBitcoinPlusPlusPeople } from './api';
  import type { BitcoinPlusPlusConference, BitcoinPlusPlusOrganization, BitcoinPlusPlusParticipation } from './api';

  export type RecipientPerson = { id: string; name: string; nostr_pubkey?: string; profile_url?: string; company?: string; participation?: BitcoinPlusPlusParticipation[] };
  let { organization, selected, canBrowseEcosystem = false, bitcoinPlusPlusURL = '', onadd, onremove }: {
    organization: BitcoinPlusPlusOrganization | null;
    selected: RecipientPerson[];
    canBrowseEcosystem?: boolean;
    bitcoinPlusPlusURL?: string;
    onadd: (person: RecipientPerson) => void;
    onremove: (pubkey: string) => void;
  } = $props();

  let dialog: HTMLDialogElement;
  let query = $state('');
  let scope = $state<'organization' | 'ecosystem' | 'selected'>('organization');
  let readiness = $state<'any' | 'verified' | 'missing'>('any');
  let conference = $state('');
  let participation = $state<'' | BitcoinPlusPlusParticipation['role']>('');
  let conferences = $state<BitcoinPlusPlusConference[]>([]);
  let results = $state<RecipientPerson[]>([]);
  let nextCursor = $state('');
  let busy = $state(false);
  let error = $state('');
  let searched = $state(false);
  let page = $state(0);
  let requestID = 0;
  let appliedQuery = '';
  const members = $derived([...(organization?.members ?? [])].sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id)));
  const selectedKeys = $derived(new Set(selected.map(person => person.nostr_pubkey)));
  const memberIDs = $derived(new Set(members.map(person => person.id)));
  const memberKeys = $derived(new Set(members.flatMap(person => person.nostr_pubkey ? [person.nostr_pubkey] : [])));
  const isMember = (person: RecipientPerson) => memberIDs.has(person.id) || Boolean(person.nostr_pubkey && memberKeys.has(person.nostr_pubkey));
  const matches = (person: RecipientPerson) => (readiness === 'any' || (readiness === 'verified') === Boolean(person.nostr_pubkey)) && `${person.name} ${person.company ?? ''}`.toLowerCase().includes(query.trim().toLowerCase());
  const filteredMembers = $derived(members.filter(matches));
  const people = $derived(scope === 'selected' ? selected.filter(matches) : scope === 'organization' ? filteredMembers : results);
  const visible = $derived(people.slice(page * 10, (page + 1) * 10));
  const preview = $derived(members.slice(0, 10));

  onMount(() => {
    let alive = true;
    if (canBrowseEcosystem) void getBitcoinPlusPlusConferences().then(value => { if (alive) conferences = value.conferences; }).catch(() => {});
    return () => { alive = false; requestID++; };
  });

  function resetSearch() {
    requestID++;
    busy = false;
    page = 0;
    results = [];
    nextCursor = '';
    error = '';
    searched = false;
  }

  async function search(append = false) {
    if (!canBrowseEcosystem || scope !== 'ecosystem') return;
    const id = ++requestID;
    const fingerprint = JSON.stringify([query.trim(), conference, participation, readiness]);
    const cursor = append && fingerprint === appliedQuery ? nextCursor : '';
    if (!cursor) { results = []; page = 0; }
    busy = true;
    error = '';
    try {
      const response = await searchBitcoinPlusPlusPeople({ q: query.trim(), conference, participation, nostr: readiness, cursor, limit: 10 });
      if (id !== requestID) return;
      // Put all matching local members first, then append the server's stable
      // directory order. Event/role matches come from the server, not guesses.
      const initial: RecipientPerson[] = cursor ? results : conference || participation ? [] : filteredMembers;
      const seenIDs = new Set(initial.map(person => person.id));
      const seenKeys = new Set(initial.flatMap(person => person.nostr_pubkey ? [person.nostr_pubkey] : []));
      const additions = response.people.filter(person => {
        if (seenIDs.has(person.id) || (person.nostr_pubkey && seenKeys.has(person.nostr_pubkey))) return false;
        seenIDs.add(person.id);
        if (person.nostr_pubkey) seenKeys.add(person.nostr_pubkey);
        return true;
      });
      results = [...initial, ...additions];
      nextCursor = response.next_cursor ?? '';
      appliedQuery = fingerprint;
      searched = true;
    } catch (cause) {
      if (id === requestID) error = cause instanceof Error ? cause.message : 'Could not search people.';
    } finally { if (id === requestID) busy = false; }
  }

  function toggle(person: RecipientPerson, checked: boolean) {
    if (!person.nostr_pubkey) return;
    if (checked) onadd(person); else onremove(person.nostr_pubkey);
  }
  function selectPage(items: RecipientPerson[], checked: boolean) {
    for (const person of items) toggle(person, checked);
  }
  function openBrowser() {
    scope = members.length ? 'organization' : canBrowseEcosystem ? 'ecosystem' : 'selected';
    resetSearch();
    dialog.showModal();
  }
</script>

{#snippet table(items: RecipientPerson[], caption: string)}
  <table>
    <caption class="sr-only">{caption}</caption>
    <thead><tr><th class="check"><input type="checkbox" aria-label="Select everyone eligible on this page" disabled={!items.some(person => person.nostr_pubkey)} checked={items.some(person => person.nostr_pubkey) && items.filter(person => person.nostr_pubkey).every(person => selectedKeys.has(person.nostr_pubkey))} indeterminate={items.some(person => person.nostr_pubkey && selectedKeys.has(person.nostr_pubkey)) && !items.filter(person => person.nostr_pubkey).every(person => selectedKeys.has(person.nostr_pubkey))} onchange={event => selectPage(items, event.currentTarget.checked)} /></th><th>Person</th><th>Status</th></tr></thead>
    <tbody>{#each items as person (person.id)}
      <tr class:chosen={person.nostr_pubkey && selectedKeys.has(person.nostr_pubkey)}>
        <td class="check"><input type="checkbox" aria-label={`Select ${person.name}`} disabled={!person.nostr_pubkey} checked={Boolean(person.nostr_pubkey && selectedKeys.has(person.nostr_pubkey))} onchange={event => toggle(person, event.currentTarget.checked)} /></td>
        <td><strong>{person.name}</strong><small>{isMember(person) ? `${organization?.name ?? 'Organization'} member` : person.company || 'Recipient'}</small>{#if person.participation?.length}<small>{person.participation.slice(0, 2).map(item => `${item.role} · ${item.conference_name || item.conference_tag}`).join(' · ')}</small>{/if}</td>
        <td><span class:ready={person.nostr_pubkey}>{person.nostr_pubkey ? selectedKeys.has(person.nostr_pubkey) ? 'Selected' : 'Ready to issue' : 'Needs a key'}</span></td>
      </tr>
    {/each}</tbody>
  </table>
{/snippet}

<section class="picker" aria-label="Choose recipients">
  <header><div><h3>{organization ? `${organization.name} members` : 'Recipients'}</h3><p>Check the people who should receive this badge.</p></div><span class="selected-count" aria-live="polite">{selected.length} selected</span></header>
  {#if preview.length}{@render table(preview, 'Organization recipients')}{:else}<p class="empty">{organization ? 'No organization members are available.' : 'Paste recipient keys below, or browse people.'}</p>{/if}
  <footer><small>{members.length > 10 ? `Showing 10 of ${members.length} members.` : `${members.length} organization members.`} Select all applies to this page only.</small><button type="button" onclick={openBrowser}>Browse people</button></footer>
  {#if members.some(person => !person.nostr_pubkey)}<p class="help">People marked “Needs a key” cannot receive a signed badge yet.{#if organization && bitcoinPlusPlusURL} <a href={`${bitcoinPlusPlusURL}/dashboard/orgs/${organization.id}/badges`} target="_blank" rel="noopener noreferrer">Manage pending grants in Bitcoin++ ↗</a>{/if}</p>{/if}
</section>

<dialog bind:this={dialog} aria-labelledby="people-title">
  <div class="browser">
    <header><div><h2 id="people-title">Choose recipients</h2><p aria-live="polite">{selected.length} selected · selections stay checked as you browse.</p></div><button type="button" onclick={() => dialog.close()}>Done</button></header>
    <form onsubmit={event => { event.preventDefault(); if (scope === 'ecosystem') void search(); }}>
      <label>Browse<select aria-label="People scope" bind:value={scope} onchange={resetSearch}><option value="organization">My organization</option><option value="selected">Selected people</option>{#if canBrowseEcosystem}<option value="ecosystem">Bitcoin++ ecosystem</option>{/if}</select></label>
      <label>Search<input aria-label="Search people" bind:value={query} oninput={resetSearch} placeholder="Name or company" /></label>
      <label>Readiness<select aria-label="Filter by Nostr key" bind:value={readiness} onchange={resetSearch}><option value="any">Everyone</option><option value="verified">Ready to issue</option><option value="missing">Needs a key</option></select></label>
      {#if scope === 'ecosystem' && canBrowseEcosystem}
        <label>Event<select aria-label="Filter by Bitcoin++ event" bind:value={conference} onchange={resetSearch}><option value="">All events</option>{#each conferences as item}<option value={item.tag}>{item.description}</option>{/each}</select></label>
        <label>Participation<select aria-label="Filter by participation" bind:value={participation} onchange={resetSearch}><option value="">All roles</option><option value="speaker">Speaker</option><option value="builder">Hackathon builder</option><option value="attendee">Attendee</option></select></label>
        <button type="submit" disabled={busy}>{busy ? 'Searching…' : 'Search directory'}</button>
      {/if}
    </form>
    {#if error}<p role="alert" class="error">{error}</p>{/if}
    {#if visible.length}{@render table(visible, 'Browse badge recipients')}{:else}<p class="empty">{scope === 'ecosystem' && !searched ? 'Search the Bitcoin++ directory to find recipients.' : 'No people match these filters.'}</p>{/if}
    <nav aria-label="People pages"><button type="button" disabled={page === 0} onclick={() => page--}>Previous</button><span>Page {page + 1} · {people.length} loaded</span><button type="button" disabled={(page + 1) * 10 >= people.length} onclick={() => page++}>Next</button></nav>
    {#if scope === 'ecosystem' && nextCursor}<button type="button" disabled={busy} onclick={() => void search(true)}>{busy ? 'Loading…' : 'Load more people'}</button>{/if}
    <p class="help">Select all applies to the current page. People without a verified Nostr key need a pending grant before they can be issued a badge.</p>
  </div>
</dialog>

<style>
  .picker { border: 1px solid var(--line); background: var(--surface); margin: 18px 0; }
  header, footer { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; padding: 16px; }
  h3 { font-size: 18px; margin: 0; } h2 { margin: 0; font: 30px var(--display); }
  p { margin: 6px 0 0; font-size: 13px; line-height: 1.5; color: var(--muted); }
  .selected-count { font-size: 12px; font-weight: 750; color: var(--teal); }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; text-align: left; }
  th { color: var(--muted); font-size: 11px; font-weight: 650; background: var(--surface-soft); }
  td, th { padding: 12px 8px; border-top: 1px solid var(--line); overflow-wrap: anywhere; }
  th:last-child { width: 26%; } .check { width: 44px; text-align: center; }
  input[type=checkbox] { width: 20px; height: 20px; accent-color: var(--violet); cursor: pointer; }
  input:disabled { cursor: not-allowed; }
  strong { display: block; font-size: 13px; } small { display: block; color: var(--muted); font-size: 11px; line-height: 1.5; }
  td span { font-size: 12px; color: var(--muted); } td span.ready { color: var(--teal); }
  .chosen { background: color-mix(in srgb, var(--aqua) 9%, var(--surface)); }
  button { min-height: 42px; padding: 9px 14px; border: 1px solid var(--line-strong); background: var(--surface); color: var(--ink); font-weight: 700; cursor: pointer; }
  button:disabled { opacity: .45; cursor: default; }
  .help, .empty { padding: 16px; } a { color: var(--violet-dark); }
  dialog { width: min(860px, calc(100vw - 24px)); max-height: calc(100dvh - 24px); padding: 0; border: 1px solid var(--line); color: var(--ink); background: var(--surface); }
  dialog::backdrop { background: rgba(20, 27, 38, .78); backdrop-filter: blur(5px); }
  .browser > header { position: sticky; top: 0; background: var(--surface); z-index: 1; border-bottom: 1px solid var(--line); }
  .browser { padding-bottom: 16px; } .browser > button { margin-left: 16px; }
  form { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; padding: 16px; background: var(--surface-soft); }
  label { display: grid; gap: 6px; font-size: 12px; }
  select, input:not([type=checkbox]) { min-width: 0; width: 100%; min-height: 42px; padding: 8px; background: var(--surface); color: var(--ink); border: 1px solid var(--line-strong); }
  nav { display: flex; justify-content: space-between; align-items: center; gap: 8px; padding: 16px; font-size: 12px; }
  .error { color: var(--coral-dark); padding: 16px; }
  .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip-path: inset(50%); }
  @media (max-width: 540px) { form { grid-template-columns: 1fr; } header, footer { padding: 12px; } th:last-child { width: 29%; } td, th { padding: 10px 5px; } .check { width: 36px; } dialog { width: 100vw; max-width: 100vw; height: 100dvh; max-height: 100dvh; margin: 0; border: 0; } }
</style>
